import React from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText, Icon, HStack, Divider } from "@chakra-ui/react";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";
import { FaChartLine, FaPiggyBank, FaShieldAlt, FaUsers, FaGem, FaRocket } from "react-icons/fa";

const FinanceWealthStrategy: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/finance/wealth-strategy"
        fallbackTitle="Wealth Building Strategy"
        fallbackDescription="Strategic financial planning for long-term wealth"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Text fontSize="sm" color="rgba(255, 255, 255, 0.7)" textTransform="uppercase" letterSpacing="wider">
              One Finance Australia
            </Text>
            <Heading size="2xl" fontWeight="bold">
              Long-Term Wealth Strategy
            </Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">Structured financial planning across property, finance, and tax — designed to compound over decades.</Text>
          </VStack>
        </Container>
      </Box>

      {/* Content Section */}
      <Container maxW="container.xl" py={20} flex="1">
        <VStack spacing={16}>
          {/* Introduction */}
          <Box textAlign="center" maxW="3xl" mx="auto">
            <Text fontSize="sm" color="#C2A878" fontWeight={600} textTransform="uppercase" letterSpacing="wider" mb={2}>
              Strategic Wealth Building
            </Text>
            <Heading size="xl" mb={4} color="#141C49">
              A System, Not a Single Decision
            </Heading>
            <Text fontSize="lg" color="gray.600">
              Our wealth strategy integrates property investment, financial structure, and tax planning
              into a single coordinated approach — built to grow and compound over the long term.
            </Text>
          </Box>

          {/* Why this approach */}
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} w="full">
            <Box p={6} bg="white" borderRadius="xl" border="2px solid" borderColor="#C2A878" textAlign="center">
              <Heading size="md" color="#141C49" mb={3}>Integrated by Design</Heading>
              <Text color="gray.600" fontSize="sm">Property, finance, and tax planning work together as one coordinated strategy — not three separate decisions.</Text>
            </Box>
            <Box p={6} bg="white" borderRadius="xl" border="2px solid" borderColor="#141C49" textAlign="center">
              <Heading size="md" color="#141C49" mb={3}>Long-Term Focus</Heading>
              <Text color="gray.600" fontSize="sm">We prioritise structures that compound over decades, not products that generate short-term returns.</Text>
            </Box>
            <Box p={6} bg="white" borderRadius="xl" border="2px solid" borderColor="#141C49" textAlign="center">
              <Heading size="md" color="#141C49" mb={3}>Education First</Heading>
              <Text color="gray.600" fontSize="sm">We explain your options clearly before recommending anything — so every decision is yours, fully informed.</Text>
            </Box>
          </SimpleGrid>

          {/* Strategy Pillars */}
          <Box w="full">
            <Heading size="lg" textAlign="center" mb={10}>Six Pillars of Wealth Strategy</Heading>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              <Box p={6} bg="white" borderRadius="lg" border="2px solid" borderColor="yellow.200" shadow="lg" _hover={{ shadow: "xl", transform: "translateY(-4px)", transition: "all 0.3s" }}>
                <HStack mb={4}>
                  <Icon as={FaChartLine} boxSize={8} color="yellow.600" />
                  <Heading size="md" color="yellow.800">Growth Assets</Heading>
                </HStack>
                <Text color="gray.600">Strategic allocation across property, shares, and alternative investments for optimal returns.</Text>
              </Box>

              <Box p={6} bg="white" borderRadius="lg" border="2px solid" borderColor="green.200" shadow="lg" _hover={{ shadow: "xl", transform: "translateY(-4px)", transition: "all 0.3s" }}>
                <HStack mb={4}>
                  <Icon as={FaPiggyBank} boxSize={8} color="green.600" />
                  <Heading size="md" color="green.800">Cash Reserve</Heading>
                </HStack>
                <Text color="gray.600">Maintain liquidity with high-interest savings and emergency funds for financial security.</Text>
              </Box>

              <Box p={6} bg="white" borderRadius="lg" border="2px solid" borderColor="blue.200" shadow="lg" _hover={{ shadow: "xl", transform: "translateY(-4px)", transition: "all 0.3s" }}>
                <HStack mb={4}>
                  <Icon as={FaShieldAlt} boxSize={8} color="blue.600" />
                  <Heading size="md" color="blue.800">Risk Management</Heading>
                </HStack>
                <Text color="gray.600">Insurance protection, diversification, and hedging strategies to safeguard your wealth.</Text>
              </Box>

              <Box p={6} bg="white" borderRadius="lg" border="2px solid" borderColor="purple.200" shadow="lg" _hover={{ shadow: "xl", transform: "translateY(-4px)", transition: "all 0.3s" }}>
                <HStack mb={4}>
                  <Icon as={FaUsers} boxSize={8} color="purple.600" />
                  <Heading size="md" color="purple.800">Estate Planning</Heading>
                </HStack>
                <Text color="gray.600">Structure your wealth for tax efficiency and smooth intergenerational transfer.</Text>
              </Box>

              <Box p={6} bg="white" borderRadius="lg" border="2px solid" borderColor="orange.200" shadow="lg" _hover={{ shadow: "xl", transform: "translateY(-4px)", transition: "all 0.3s" }}>
                <HStack mb={4}>
                  <Icon as={FaGem} boxSize={8} color="orange.600" />
                  <Heading size="md" color="orange.800">Tax Optimisation</Heading>
                </HStack>
                <Text color="gray.600">Minimize tax liability through smart structures, deductions, and investment strategies.</Text>
              </Box>

              <Box p={6} bg="white" borderRadius="lg" border="2px solid" borderColor="red.200" shadow="lg" _hover={{ shadow: "xl", transform: "translateY(-4px)", transition: "all 0.3s" }}>
                <HStack mb={4}>
                  <Icon as={FaRocket} boxSize={8} color="red.600" />
                  <Heading size="md" color="red.800">Growth Acceleration</Heading>
                </HStack>
                <Text color="gray.600">Leverage equity, compound returns, and strategic reinvestment for accelerated growth.</Text>
              </Box>
            </SimpleGrid>
          </Box>

          {/* Process Timeline */}
          <Box w="full" bg="gray.50" p={10} borderRadius="xl">
            <Heading size="lg" textAlign="center" mb={8}>Your Wealth Journey</Heading>
            <VStack spacing={6} align="stretch">
              <HStack>
                <Box bg="yellow.500" color="white" w={10} h={10} borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontWeight={700}>1</Box>
                <Box flex={1}>
                  <Heading size="sm" mb={1}>Discovery & Assessment</Heading>
                  <Text fontSize="sm" color="gray.600">Assess current position, goals, risk tolerance, and timeline</Text>
                </Box>
              </HStack>
              <Divider />
              <HStack>
                <Box bg="yellow.500" color="white" w={10} h={10} borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontWeight={700}>2</Box>
                <Box flex={1}>
                  <Heading size="sm" mb={1}>Strategy Design</Heading>
                  <Text fontSize="sm" color="gray.600">Create customized plan with asset allocation and investment structure</Text>
                </Box>
              </HStack>
              <Divider />
              <HStack>
                <Box bg="yellow.500" color="white" w={10} h={10} borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontWeight={700}>3</Box>
                <Box flex={1}>
                  <Heading size="sm" mb={1}>Implementation</Heading>
                  <Text fontSize="sm" color="gray.600">Execute strategy with financing, purchases, and structure setup</Text>
                </Box>
              </HStack>
              <Divider />
              <HStack>
                <Box bg="yellow.500" color="white" w={10} h={10} borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontWeight={700}>4</Box>
                <Box flex={1}>
                  <Heading size="sm" mb={1}>Ongoing Optimisation</Heading>
                  <Text fontSize="sm" color="gray.600">Regular reviews, rebalancing, and adjustments as markets and goals evolve</Text>
                </Box>
              </HStack>
            </VStack>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceWealthStrategy;
